import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Dashboard as DashboardData } from '../lib/api'
import { fmtAgo, fmtDateTime, fmtMoney, fmtNumber, humanReason, maskMsisdn } from '../lib/format'
import { Card, Empty, HealthPill, Loading, SignalBars, Spark, Stat } from '../components/ui'

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/api/admin/dashboard'),
    refetchInterval: 5_000,
  })

  if (isLoading || !data) return <Loading rows={8} />

  const { counts, fleet, throughput, recentFailures } = data
  const sentLastHour = throughput.at(-1)?.count ?? 0

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="page-sub">
            Live fleet health, queue depth and anything that has failed recently.
          </div>
        </div>
      </div>

      {counts.pendingEnrolments > 0 && (
        <div className="banner info">
          <div>
            <strong>
              {counts.pendingEnrolments} device{counts.pendingEnrolments === 1 ? '' : 's'} waiting for approval.
            </strong>{' '}
            A new device cannot send anything until an administrator approves it.{' '}
            <Link to="/devices" style={{ textDecoration: 'underline' }}>
              Review the enrolment queue
            </Link>
          </div>
        </div>
      )}

      {counts.online === 0 && counts.total > 0 && (
        <div className="banner danger">
          <div>
            <strong>No device is online.</strong> Messages are still being accepted and queued, but nothing can be
            sent until at least one device reconnects.
          </div>
        </div>
      )}

      <div className="grid cols-4">
        <Stat
          label="Devices online"
          value={
            <>
              {counts.online}
              <span className="faint" style={{ fontSize: 18 }}>
                /{counts.total}
              </span>
            </>
          }
          foot={counts.dark > 0 ? `${counts.dark} dark` : 'all healthy'}
          tone={counts.online === 0 ? 'danger' : counts.dark > 0 ? 'warn' : undefined}
        />
        <Stat
          label="Queue depth"
          value={fmtNumber(counts.queueDepth)}
          foot={counts.parked > 0 ? `${counts.parked} parked — no device available` : 'nothing waiting'}
          tone={counts.parked > 0 ? 'warn' : undefined}
        />
        <Stat label="Sent today" value={fmtNumber(counts.sentToday)} foot={`${sentLastHour} in the last hour`} />
        <Stat
          label="Open alerts"
          value={fmtNumber(counts.openAlerts)}
          foot={counts.openAlerts === 0 ? 'nothing to look at' : 'needs attention'}
          tone={counts.openAlerts > 0 ? 'danger' : undefined}
        />
      </div>

      <Card title="Throughput — last 24 hours">
        <Spark points={throughput.map((t) => t.count)} />
        <div className="row between faint small" style={{ marginTop: 8 }}>
          <span>24h ago</span>
          <span>{fmtNumber(throughput.reduce((a, b) => a + b.count, 0))} submitted</span>
          <span>now</span>
        </div>
      </Card>

      <Card title="Fleet" flush>
        {fleet.length === 0 ? (
          <Empty title="No approved devices yet" hint="Flash a device and approve it from the Devices page." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Health</th>
                  <th>Signal</th>
                  <th>Balance</th>
                  <th className="num">Today</th>
                  <th className="num">Failed 1h</th>
                  <th>Last heartbeat</th>
                </tr>
              </thead>
              <tbody>
                {fleet.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link to={`/devices/${d.id}`} className="row" style={{ gap: 8 }}>
                        <strong>{d.name}</strong>
                        {!d.isEnabled && <span className="chip">disabled</span>}
                      </Link>
                      {d.msisdn && <div className="faint small mono">{d.msisdn}</div>}
                    </td>
                    <td>
                      <HealthPill state={d.healthState} reason={d.darkReason} />
                      {d.healthState === 'Dark' && d.nextProbeAt && (
                        <div className="faint small" style={{ marginTop: 3 }}>
                          next probe {fmtAgo(d.nextProbeAt)}
                        </div>
                      )}
                    </td>
                    <td>
                      <SignalBars rssi={d.rssi} />
                    </td>
                    <td className="num">{fmtMoney(d.balanceAmount, d.balanceCurrency)}</td>
                    <td className="num">{fmtNumber(d.sentToday)}</td>
                    <td className="num" style={{ color: d.failedLastHour > 0 ? 'rgb(var(--danger))' : undefined }}>
                      {d.failedLastHour || '—'}
                    </td>
                    <td className="faint small">{d.isConnected ? fmtAgo(d.lastHeartbeatAt ?? null) : 'offline'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Recent failures" flush>
        {recentFailures.length === 0 ? (
          <Empty title="Nothing has failed recently" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>To</th>
                  <th>Reason</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {recentFailures.map((f) => (
                  <tr key={f.id}>
                    <td className="faint small">{fmtDateTime(f.failedAt)}</td>
                    <td className="mono">{maskMsisdn(f.msisdn)}</td>
                    <td>
                      <span className="pill danger">{humanReason(f.reasonCode)}</span>
                    </td>
                    <td className="muted small">{f.reasonDetail ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// The type is re-exported under its page name for the router's lazy import.
export type { DashboardData }
